// src/components/SignatureCard.js
//
// v3.9.9.8: ONE parameterized signature/status card for every agreement type —
// replaces the near-identical VoiceSignatureCard / FlexITSignatureCard /
// BundleSignatureCard (Shaun CONSOLIDATE directive, CLAUDE.md §13). All type
// variance lives in TYPE_CONFIG below; callers pass only `docType` + the common
// props. States: idle, pending, signed, completed, declined, cancelled, expired.
//
// Behavior is the union of the three old cards (no regressions):
//   - forwardRef + openSendModal() for the parent's Lock-Rates-First "Send Anyway"
//     override (Voice/Bundle used it; harmless for FlexIT which passes no ref).
//   - Lock-Rates-First gate via ratesLocked/onLockRatesPrompt (default ratesLocked
//     = true → no gate, matching the old FlexIT card).
//   - Reconcile-aware completed state (was Voice-only; now all types): auto-reconcile
//     on open when completed-but-no-PDF, Refresh reconciles, Get-Signed-PDF fetches.
//   - Generic helpers: getLatestDoc(quoteId, docType) + refreshDocStatus (type-
//     independent) + reconcileCompletion; send routes by docType in SendForSignatureModal.

import React, { useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import {
  getLatestDoc,
  refreshDocStatus,
  reconcileCompletion,
  sendSignatureReminder,
  STATUS_LABELS,
  fmtUsd,
} from '../lib/signatureDocs';
import { logActivity } from '../lib/supabase';
import SendForSignatureModal from './SendForSignatureModal';

// All per-type variance in one place — do NOT fork this component per type.
const TYPE_CONFIG = {
  voice_quote: {
    accent: '#7c3aed',
    headerTitle: 'SignWell — Voice Service Agreement',
    headerSubtitle: 'Service config + recurring & one-time charges + acceptance terms + signatures',
    sendLabel: '✏ Send Voice Service Agreement for Signature',
    upfrontLabel: 'Upfront NRC',
    autoPayNote: '⚡ When the client signs, a Stripe payment link for the upfront NRC will be automatically generated and emailed (if enabled in Admin → Integrations → Stripe). Recurring monthly charges begin upon service activation.',
  },
  flexit_quote: {
    accent: '#0ea5e9',
    headerTitle: 'SignWell — Agreement',
    headerSubtitle: 'FlexIT order + acceptance terms + signatures',
    sendLabel: '✏ Send Agreement for Signature',
    upfrontLabel: 'Amount',
    autoPayNote: '⚡ When the client signs, a Stripe payment link will be automatically generated and emailed (if enabled in Admin → Integrations → Stripe).',
  },
  bundle_quote: {
    accent: '#4f46e5',
    headerTitle: 'SignWell — Bundle Service Agreement',
    headerSubtitle: 'IT + Voice services + recurring & one-time charges + acceptance terms + signatures',
    sendLabel: '✏ Send Bundle Service Agreement for Signature',
    upfrontLabel: 'Upfront NRC',
    autoPayNote: '⚡ When the client signs, a Stripe payment link for the combined upfront NRC (IT onboarding + Voice hardware/activation/porting) will be automatically generated and emailed (if enabled in Admin → Integrations → Stripe). Combined recurring monthly charges begin upon service activation.',
  },
};

export default forwardRef(function SignatureCard({
  docType,
  quoteId,
  quoteStatus,
  quoteNumber,
  clientName,
  clientContact,
  clientEmail,
  upfrontAmount,
  userId,
  ratesLocked = true,      // default true → no lock gate (old FlexIT behavior)
  onLockRatesPrompt,
}, ref) {
  const cfg = TYPE_CONFIG[docType] || TYPE_CONFIG.flexit_quote;

  const [doc, setDoc]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [msg, setMsg]         = useState('');
  const [showModal, setShow]  = useState(false);

  const isApproved = quoteStatus === 'approved';
  const lockGate   = !ratesLocked && isApproved;

  useImperativeHandle(ref, () => ({
    openSendModal: () => {
      if (!isApproved) { setMsg('Quote must be approved before sending for signature'); return; }
      setShow(true);
    },
  }), [isApproved]);

  // Initial load (+ auto-reconcile a completed-but-unfinalized doc — missed webhook)
  useEffect(() => {
    if (!quoteId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      const d = await getLatestDoc(quoteId, docType);
      if (cancelled) return;
      setDoc(d);
      setLoading(false);
      if (d && String(d.status || '').toLowerCase() === 'completed' && !d.completed_pdf_url && !d.completion_reconciled) {
        try {
          await reconcileCompletion(d.id);
          const fresh = await getLatestDoc(quoteId, docType);
          if (!cancelled && fresh) setDoc(fresh);
        } catch { /* best-effort */ }
      }
    })();
    return () => { cancelled = true; };
  }, [quoteId, docType]);

  // Live polling while pending
  useEffect(() => {
    if (!doc?.id) return;
    if (['completed', 'cancelled', 'declined', 'expired'].includes(String(doc.status || '').toLowerCase())) return;
    let cancelled = false;
    const interval = setInterval(async () => {
      try {
        await refreshDocStatus(quoteId, doc.id);
        if (cancelled) return;
        const fresh = await getLatestDoc(quoteId, docType);
        if (!cancelled) setDoc(fresh);
      } catch { /* swallow polling errors */ }
    }, 15000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [doc?.id, doc?.status, quoteId, docType]);

  async function handleManualRefresh() {
    if (!doc?.id) return;
    setBusy(true); setMsg('');
    try {
      await refreshDocStatus(quoteId, doc.id);
      let fresh = await getLatestDoc(quoteId, docType);
      if (fresh && String(fresh.status || '').toLowerCase() === 'completed' && !fresh.completed_pdf_url) {
        await reconcileCompletion(fresh.id);
        fresh = await getLatestDoc(quoteId, docType);
      }
      setDoc(fresh);
      setMsg('✓ Status refreshed');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) { setMsg('✗ ' + e.message); }
    setBusy(false);
  }

  async function handleDownloadSigned() {
    if (doc?.completed_pdf_url) { window.open(doc.completed_pdf_url, '_blank', 'noopener'); return; }
    if (!doc?.id) return;
    setBusy(true); setMsg('');
    try {
      await reconcileCompletion(doc.id);
      const fresh = await getLatestDoc(quoteId, docType);
      setDoc(fresh);
      if (fresh?.completed_pdf_url) window.open(fresh.completed_pdf_url, '_blank', 'noopener');
      else setMsg('Signed PDF isn’t ready from SignWell yet — try Refresh in a moment.');
    } catch (e) { setMsg('✗ ' + e.message); }
    setBusy(false);
  }

  async function handleReminder() {
    if (!doc?.id) return;
    setBusy(true); setMsg('');
    try {
      await sendSignatureReminder(doc.id);
      setMsg('✓ Reminder sent to ' + doc.client_email);
      setTimeout(() => setMsg(''), 2500);
    } catch (e) { setMsg('✗ ' + e.message); }
    setBusy(false);
  }

  function handleNewDocument() {
    if (!isApproved) { setMsg('Quote must be approved before sending for signature'); return; }
    if (!ratesLocked && onLockRatesPrompt) { onLockRatesPrompt(); return; }
    setShow(true);
  }

  function handleSent(record) {
    setDoc(record);
    setShow(false);
    setMsg(`✓ Sent to ${record.client_email}`);
    setTimeout(() => setMsg(''), 3000);
    logActivity({
      action:     'SIGNATURE_REQUEST_SENT',
      entityType: 'quote',
      entityId:   quoteId,
      entityName: clientName || quoteNumber || null,
      metadata: {
        document_type:   docType,
        signwell_doc_id: record.id || record.document_id || null,
        recipient_email: record.client_email,
        recipient_name:  record.client_name || clientContact || null,
        quote_number:    quoteNumber,
        upfront_amount:  upfrontAmount,
        test_mode:       record.test_mode === true,
      },
    }).catch(e => console.warn('Activity log failed:', e?.message));
  }

  const modal = showModal && (
    <SendForSignatureModal
      open={showModal}
      onClose={() => setShow(false)}
      onSent={handleSent}
      quoteId={quoteId}
      quoteNumber={quoteNumber}
      clientName={clientName}
      clientContact={clientContact}
      clientEmail={clientEmail}
      upfrontAmount={upfrontAmount}
      userId={userId}
      docType={docType}
    />
  );

  // ── Render ──
  if (!quoteId) {
    return (
      <div style={cardWrap('#e5e7eb')}>
        <CardHeader cfg={cfg} />
        <div style={{ padding: '8px 12px', fontSize: 11, color: '#9ca3af' }}>Save the quote first to enable signature sending.</div>
      </div>
    );
  }
  if (loading) {
    return (
      <div style={cardWrap('#e5e7eb')}>
        <CardHeader cfg={cfg} />
        <div style={{ padding: '8px 12px', fontSize: 11, color: '#9ca3af' }}>Loading…</div>
      </div>
    );
  }

  // ── No document yet ──
  if (!doc) {
    return (
      <div style={cardWrap('#e5e7eb')}>
        <CardHeader cfg={cfg} />
        <div style={{ padding: '12px' }}>
          {!isApproved ? (
            <div style={{ fontSize: 11, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, padding: '8px 10px', marginBottom: 8 }}>
              ⚠ Quote must be in <strong>approved</strong> status before sending for signature.
              Current status: <strong>{quoteStatus || 'draft'}</strong>. Use Send for Review or self-approve first.
            </div>
          ) : null}
          {lockGate ? (
            <div style={{ fontSize: 11, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, padding: '8px 10px', marginBottom: 8 }}>
              ⚠ <strong>OOS rates aren&rsquo;t locked yet.</strong> Lock rates from the Out-of-Scope Rate Schedule card before sending — the Service Order won&rsquo;t generate without locked rates.
            </div>
          ) : null}
          <button
            onClick={handleNewDocument}
            disabled={!isApproved || busy}
            title={!isApproved ? 'Quote must be approved first' : lockGate ? 'OOS rates aren’t locked yet — clicking will prompt to lock first' : ''}
            style={{ ...btnPrimary(cfg), width: '100%', padding: '8px 14px', opacity: (!isApproved || busy) ? 0.5 : 1, border: lockGate ? '2px solid #f59e0b' : 'none' }}>
            {lockGate ? '⚠ ' : ''}{cfg.sendLabel}
          </button>
          {msg && <div style={{ fontSize: 10, fontWeight: 600, marginTop: 8, color: msg.startsWith('✓') ? '#166534' : '#dc2626' }}>{msg}</div>}
        </div>
        {modal}
      </div>
    );
  }

  // ── Have a document ──
  const status = String(doc.status || '').toLowerCase();
  const isPending    = ['sent', 'viewed', 'signed'].includes(status);
  const isCompleted  = status === 'completed';
  const failed       = status === 'declined' || status === 'cancelled' || status === 'expired';
  const borderColor  = isCompleted ? '#bbf7d0' : isPending ? '#fde68a' : failed ? '#fecaca' : '#e5e7eb';

  return (
    <>
      <div style={cardWrap(borderColor)}>
        <CardHeader cfg={cfg} signed={isCompleted} pending={isPending} failed={failed} testMode={doc.test_mode} />

        {/* Status row */}
        <div style={{ padding: '10px 12px', background: isCompleted ? '#f0fdf4' : isPending ? '#fffbeb' : failed ? '#fef2f2' : '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: isCompleted ? '#166534' : isPending ? '#a16207' : '#991b1b', marginBottom: 4 }}>
            {STATUS_LABELS[status] || status}
          </div>
          <div style={{ fontSize: 10, color: '#6b7280', lineHeight: 1.6 }}>
            <div>Sent to: <strong>{doc.client_email}</strong></div>
            {doc.created_at && <div>On {new Date(doc.created_at).toLocaleString()}</div>}
            {doc.signed_at && <div style={{ color: '#166534', fontWeight: 600 }}>Client signed {new Date(doc.signed_at).toLocaleString()}</div>}
            {doc.completed_at && <div style={{ color: '#166534', fontWeight: 600 }}>All signatures complete {new Date(doc.completed_at).toLocaleString()}</div>}
            {doc.countersign_required && <div>Countersign: {doc.countersigned ? '✓ Done' : '○ Pending'}</div>}
            {doc.upfront_amount && <div>{cfg.upfrontLabel}: {fmtUsd(doc.upfront_amount)}</div>}
          </div>
        </div>

        {/* Actions */}
        <div style={{ padding: '10px 12px' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {isPending && (
              <>
                <button onClick={handleReminder} disabled={busy} style={btnSecondary}>📧 Send Reminder</button>
                <button onClick={handleManualRefresh} disabled={busy} style={btnSecondary}>↻ Refresh</button>
              </>
            )}
            {isCompleted && (
              <>
                {doc.completed_pdf_url
                  ? <a href={doc.completed_pdf_url} target="_blank" rel="noopener noreferrer" style={btnLink}>📄 Download Signed PDF ↗</a>
                  : <button onClick={handleDownloadSigned} disabled={busy} style={btnLink}>📄 Get Signed PDF</button>}
                <button onClick={handleManualRefresh} disabled={busy} style={btnSecondary}>↻ Refresh</button>
              </>
            )}
            {failed && (
              <button
                onClick={handleNewDocument}
                disabled={busy || !isApproved}
                title={!isApproved ? 'Quote must be approved first' : lockGate ? 'OOS rates aren’t locked yet — clicking will prompt to lock first' : ''}
                style={{ ...btnPrimary(cfg), border: lockGate ? '2px solid #f59e0b' : 'none' }}>
                {lockGate ? '⚠ ' : ''}Send New Agreement
              </button>
            )}
            {(isCompleted || isPending) && isApproved && (
              <button
                onClick={handleNewDocument}
                disabled={busy}
                title={lockGate ? 'OOS rates aren’t locked yet — clicking will prompt to lock first' : ''}
                style={{ ...btnSecondary, marginLeft: 'auto', border: lockGate ? '2px solid #f59e0b' : btnSecondary.border }}>
                {lockGate ? '⚠ ' : ''}+ Send Another
              </button>
            )}
          </div>

          {msg && <div style={{ fontSize: 10, fontWeight: 600, marginTop: 8, color: msg.startsWith('✓') ? '#166534' : '#dc2626' }}>{msg}</div>}

          {!isCompleted && !failed && (
            <div style={{ fontSize: 9, color: '#475569', marginTop: 8, fontStyle: 'italic', borderTop: '1px solid #f1f5f9', paddingTop: 6 }}>
              {cfg.autoPayNote}
            </div>
          )}
          {doc?.payment_id && (
            <div style={{ fontSize: 9, color: '#166534', marginTop: 8, fontWeight: 600, borderTop: '1px solid #f1f5f9', paddingTop: 6 }}>
              ✓ Stripe payment link automatically generated. See the Stripe Prepayment card below for status.
            </div>
          )}
        </div>
      </div>
      {modal}
    </>
  );
});

function CardHeader({ cfg, signed, pending, failed, testMode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#0f1e3c', borderRadius: '6px 6px 0 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 22, height: 22, background: cfg.accent, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'white' }}>SW</div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'white' }}>{cfg.headerTitle}</div>
          <div style={{ fontSize: 8, color: '#9ca3af' }}>{cfg.headerSubtitle}</div>
        </div>
      </div>
      {testMode && <span style={{ fontSize: 9, fontWeight: 700, color: '#a16207', background: '#fef3c7', padding: '2px 7px', borderRadius: 3 }}>TEST</span>}
      {signed   && <span style={{ fontSize: 9, fontWeight: 700, color: '#166534', background: '#dcfce7', padding: '2px 7px', borderRadius: 3 }}>SIGNED ✓</span>}
      {pending  && !signed && <span style={{ fontSize: 9, fontWeight: 700, color: '#a16207', background: '#fef3c7', padding: '2px 7px', borderRadius: 3 }}>PENDING</span>}
      {failed   && <span style={{ fontSize: 9, fontWeight: 700, color: '#991b1b', background: '#fee2e2', padding: '2px 7px', borderRadius: 3 }}>!</span>}
    </div>
  );
}

const cardWrap = (border) => ({ background: 'white', border: `1px solid ${border}`, borderRadius: 6, marginBottom: 10, overflow: 'hidden' });
const btnPrimary = (cfg) => ({ padding: '6px 12px', background: cfg.accent, color: 'white', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer' });
const btnSecondary = { padding: '6px 12px', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer' };
const btnLink = { padding: '6px 12px', background: 'transparent', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer', textDecoration: 'none', display: 'inline-block' };
